import { ArrowRight, Bot, FolderPlus, Layers, Plus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Alert, Badge, Button, Card, EmptyState, Field, Input, Modal, Skeleton, Textarea } from '../components/ui';
import { ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useCreateProject, useProjects } from '../lib/queries';
import { timeAgo } from '../lib/utils';

function slugPreview(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'project'
  );
}

export default function ProjectsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: projects, isLoading } = useProjects();
  const createProject = useCreateProject();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      const project = await createProject.mutateAsync({
        name,
        slug: slug.trim() || undefined,
        description: description.trim() || undefined,
      });
      toast.success(`Created ${project.name}`);
      setOpen(false);
      setName('');
      setSlug('');
      setDescription('');
    } catch (err) {
      setError(err instanceof ApiError ? [err.message, ...err.issues].join(' — ') : 'Could not create the project');
    }
  }

  return (
    <div className="h-full scroll-y">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4">
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-ink-100">Projects</h1>
            <p className="mt-1 text-sm text-ink-400">
              Each project gets its own API namespace, datasources, dashboards and member list.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2 self-start sm:self-auto">
            {/* The project list is the instance's front door, so the
                instance-wide things belong here rather than inside one project. */}
            {user?.globalRole === 'superadmin' && (
              <Button
                variant="secondary"
                icon={<Bot className="size-4" />}
                onClick={() => navigate('/instance/mcp')}
              >
                MCP
              </Button>
            )}
            <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setOpen(true)}>
              New project
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="mt-8 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-36" />
            ))}
          </div>
        ) : !projects || projects.length === 0 ? (
          <Card className="mt-8">
            <EmptyState
              icon={<FolderPlus className="size-5" />}
              title="No projects yet"
              description={
                user?.globalRole === 'superadmin'
                  ? 'Create your first project, then build an endpoint on the canvas and publish it.'
                  : 'You have not been added to any project yet. Ask an admin for an invite.'
              }
              action={
                <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setOpen(true)}>
                  Create a project
                </Button>
              }
            />
          </Card>
        ) : (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((project) => (
              <Link key={project.id} to={`/projects/${project.id}`} className="group">
                <Card className="flex h-full flex-col p-5 transition-colors hover:border-ink-700 hover:bg-ink-850">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex size-9 items-center justify-center rounded-lg bg-brand-500/12 text-brand-400">
                      <Layers className="size-4" />
                    </div>
                    <Badge tone={project.role === 'viewer' ? 'neutral' : 'brand'}>{project.role}</Badge>
                  </div>

                  <h3 className="mt-4 truncate text-sm font-semibold text-ink-100">{project.name}</h3>
                  <p className="mt-1 line-clamp-2 flex-1 text-xs leading-relaxed text-ink-400">
                    {project.description || 'No description yet.'}
                  </p>

                  <div className="mt-4 flex items-center justify-between border-t border-ink-800 pt-3">
                    <code className="truncate font-mono text-[10px] text-ink-500">/api/v1/{project.slug}</code>
                    <span className="flex shrink-0 items-center gap-1 text-[10px] text-ink-500">
                      {timeAgo(project.updatedAt)}
                      <ArrowRight className="size-3 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="New project"
        description="The slug becomes the public path prefix for every endpoint you publish."
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={onCreate} loading={createProject.isPending} disabled={!name.trim()}>
              Create project
            </Button>
          </>
        }
      >
        <form onSubmit={onCreate} className="space-y-4">
          <Field label="Name" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Internal Tools" autoFocus required />
          </Field>

          <Field label="Slug" hint={`Endpoints will live at /api/v1/${slug.trim() || slugPreview(name)}/…`}>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              placeholder={slugPreview(name)}
            />
          </Field>

          <Field label="Description">
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="What this project is for."
            />
          </Field>

          {error && <Alert tone="danger">{error}</Alert>}
        </form>
      </Modal>
    </div>
  );
}
